import { ICtaTemplateApi } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { IAnalyticsNode, ICampaignAnalytics, TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { IMatrixGridColumn, IMatrixGridRow } from '@shared/components/matrix-grid/matrix-grid.interface';
import { ISlotBarSlot, TBarTone } from '@shared/components/slot-bar/slot-bar.interface';
import { TTileContentStatus } from '@shared/components/social-content-tile/social-content-tile.interface';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { DeliverableUtilitiesService } from '@utilities/deliverable-utilities.service';
import { ICampaignDetailsDeliverablesTableRow } from '../campaign-details-deliverables/campaign-details-deliverables.interface';
import { ICampaignAnalyticsFilter } from '../campaign-details.interface';
import { ICampaignExportConfig } from '../campaign-export-config/campaign-export-config.interface';

export enum EGoalsLens {
  deliverables = 'deliverables',
  kpis = 'kpis',
}

export enum EGoalsSortKey {
  creator = 'creator',
  planComplete = 'planComplete',
}

export interface IGoalsSort {
  key: EGoalsSortKey;
  direction: 'asc' | 'desc';
}

export type TGoalsFrequency = 'everyStream' | 'optional' | 'required';

/** notInPlan: creator has no planned CTA for the group; optional: only no_expectations executions. */
export type TGoalsCellKind = 'notInPlan' | 'optional' | 'everyStream' | 'required';

export type TGoalsSlotKind = 'counted' | 'free';

export type TGoalsKpiState = 'reached' | 'partial' | 'below' | 'noData';

export interface IGoalsMatrixParams {
  analyticsUtilities: Pick<CampaignAnalyticsUtilitiesService, 'getAnalyticsStatusWithStatusSet'>;
  ctaTemplates: ICtaTemplateApi[];
  deliverableUtilities: Pick<DeliverableUtilitiesService, 'getDeliverableCollectionConfigs' | 'isTrackingDeliverable'>;
  exportConfig: ICampaignExportConfig | null;
  filter: ICampaignAnalyticsFilter | null;
  isDateRangeNarrowed: boolean;
  nodes: IAnalyticsNode[];
  shell: ICampaignAnalytics | null;
}

export interface IGoalsMatrix {
  columns: IGoalsColumn[];
  hasKpiGoals: boolean;
  rows: IGoalsRow[];
}

export interface IGoalsTemplateKpi {
  kpiType: TExpectedKpi;
  target: number;
}

export interface IGoalsColumn {
  ctaId: string;
  deliverableCount: number;
  footer: Record<EGoalsLens, IGoalsFooter>;
  frequency: TGoalsFrequency;
  /** A "required" group that also has an "always" execution: every stream adds a slot on top of the count. */
  hasEveryStream: boolean;
  hasKpiGoals: boolean;
  name: string;
  requiredCount: number;
  templateKpis: IGoalsTemplateKpi[];
}

export interface IGoalsFooterSegment {
  tone: TBarTone;
  value: number;
}

export interface IGoalsFooter {
  completeCreators: number;
  creatorsInPlan: number;
  fulfilled: number;
  /** Required slots still free (planned or missing). */
  open: number;
  percent: number;
  required: number;
  segments: IGoalsFooterSegment[];
}

export interface IGoalsPlanProgress {
  complete: number;
  total: number;
}

export interface IGoalsRow {
  cells: Record<string, IGoalsCell>;
  creatorIcon: string | null;
  creatorId: string;
  creatorName: string;
  planProgress: Record<EGoalsLens, IGoalsPlanProgress>;
}

export interface IGoalsSlotDeliverable {
  deliverableId: string;
  name: string;
  status: EAnalyticsStatus;
  type: EDeliverableType;
}

export interface IGoalsSlotKpi {
  expectedKpiId: string;
  key: string;
  kpiType: TExpectedKpi;
  state: Exclude<TGoalsKpiState, 'partial'>;
  target: number;
  value: number | null;
}

export interface IGoalsSlot {
  analyticsNode: IAnalyticsNode | null;
  deliverables: IGoalsSlotDeliverable[];
  executionTime: string | null;
  /** False when the slot's execution sets no KPI goals: the KPI lens shows it grey and leaves it out of its counts. */
  hasKpiGoals: boolean;
  kind: TGoalsSlotKind;
  kpiState: TGoalsKpiState | null;
  kpis: IGoalsSlotKpi[];
  platformFeature: EPlatformFeature | null;
  status: EAnalyticsStatus | null;
}

export interface IGoalsCellLens {
  /** Label numerator: Deliverables = delivered contents, KPIs = contents with every target reached. */
  done: number;
  hasGoals: boolean;
  /** Deliverables: problem + failed contents. KPIs: partial + below contents. */
  issues: number;
  isComplete: boolean;
  noData: number;
  /** KPIs: slots without KPI goals, left out of done / complete / issues / no data / footer. Deliverables: none. */
  slotExcluded: boolean[];
  slotHidden: boolean[];
  tones: TBarTone[];
}

export interface IGoalsDeliverableRow {
  deliverableId: string;
  fulfilled: number;
  hidden: boolean[];
  /** Per slot: the deliverable's result as an i18n key; null where it isn't part of the content or the report hides it. */
  labels: (string | null)[];
  name: string;
  tones: TBarTone[];
  total: number;
  type: EDeliverableType;
}

export interface IGoalsKpiRow {
  average: number | null;
  expectedKpiId: string;
  key: string;
  kpiType: TExpectedKpi;
  /** Per slot: the target's result as an i18n key; null where the content has no such target or the report hides it. */
  labels: (string | null)[];
  reached: number;
  target: number;
  tones: TBarTone[];
  total: number;
}

export interface IGoalsCell {
  counted: number;
  creatorId: string;
  ctaId: string;
  ctaName: string;
  deliverableRows: IGoalsDeliverableRow[];
  extraSlots: IGoalsSlot[];
  free: number;
  kind: TGoalsCellKind;
  kpiRows: IGoalsKpiRow[];
  lenses: Record<EGoalsLens, IGoalsCellLens>;
  /** Contents of an Optional (no_expectations) cell. */
  optionalDelivered: number;
  required: number;
  slots: IGoalsSlot[];
  subRowCount: number;
}

export type TGoalsGridColumnKind = 'creator' | 'group' | 'placeholder' | 'plan';

export interface IGoalsGridColumn extends IMatrixGridColumn {
  group: IGoalsColumn | null;
  kind: TGoalsGridColumnKind;
}

export interface IGoalsGridRow extends IGoalsRow, IMatrixGridRow {}

export interface IGoalsNote {
  /** With matching types: also selects these content statuses on the Contents tab ("n with issues"). */
  contentStatuses?: TTileContentStatus[];
  key: string;
  /** Set when the note links to the creator's contents of this group with these matching types. */
  matchingTypes?: EMatchingType[];
  params?: Record<string, number>;
}

/** Emitted when a slot block (group bar or sub-row) asks for the CTA analytics dialog. */
export interface IGoalsAnalyticsRequest {
  analyticsNode: IAnalyticsNode;
  ctaId: string;
  deliverableId?: string;
  deliverableType?: EDeliverableType;
  expectedKpiId?: string;
  kpiType?: TExpectedKpi;
}

export interface IGoalsSubRowView {
  bar: ISlotBarSlot[];
  /** Deliverable sub-rows only: the label links to the Deliverables tab filtered to this type. */
  deliverableType?: EDeliverableType;
  id: string;
  label: string;
  name: string;
  /** One per slot, in slot order; null where the block opens nothing. */
  requests: (IGoalsAnalyticsRequest | null)[];
}

/** A Deliverables-tab link: every filter left out stays open (no group = all groups, no row = all creators). */
export interface IGoalsDeliverablesLink {
  ctaId?: string;
  deliverableType?: EDeliverableType;
  /**
   * The whole group (all but unrelated) instead of what the blocks count: an Optional group's header and tag, whose
   * bonus rows are its deliveries. Other groups leave bonus out: there it may be a content delivered for another group.
   */
  groupWide?: boolean;
  row?: IGoalsGridRow;
}

/** A Contents-tab link: the matching types and content statuses it selects there. */
export interface IGoalsContentsLink {
  contentStatuses?: TTileContentStatus[];
  matchingTypes: EMatchingType[];
}

/** Deliverables-lens footer: the first part of its line and where it links (null: plain text). */
export interface IGoalsFooterSummary {
  labelKey: string;
  link: IGoalsContentsLink | null;
  params: Record<string, number>;
}

/** Which rows a Goals link selects on the Deliverables tab: what the blocks count, or the whole group. */
export type TGoalsDeliverablesLinkScope = 'counted' | 'group';

export type TGoalsDeliverablesLinkRow = Pick<
  ICampaignDetailsDeliverablesTableRow,
  'creatorId' | 'ctaTemplateId' | 'deliverableType' | 'matchingType'
>;

/** The Deliverables-tab filters a Goals link sets; one left out matches any. */
export interface IGoalsDeliverablesLinkFilters {
  creatorId?: string;
  ctaId?: string;
  deliverableType?: EDeliverableType;
}

/** A hover-card line "View n … in Contents →": its label key under `pages.campaignDetails.goals.card`. */
export interface IGoalsCellLink {
  key: string;
  link: IGoalsContentsLink;
  params: Record<string, number>;
}

export interface IGoalsCellView {
  bar: ISlotBarSlot[];
  links: IGoalsCellLink[];
  /** The cell's notes as one plain line: the hover card's first line and part of the count's label. */
  summary: string;
  subRows: IGoalsSubRowView[];
}

export interface IGoalsColumnHeaderView {
  frequency: IGoalsNote;
  subText: string;
  subTooltip: string;
}

export interface IGoalsDeepLink {
  creatorId: string | null;
  revision: number;
}
